import { ApolloLink, Observable } from '@apollo/client';
import Raven from 'raven-js';

type Operation = Parameters<
  NonNullable<ConstructorParameters<typeof ApolloLink>[0]>
>[0];

const getOperationDetails = (operation: Operation) => {
  const { operationName, variables } = operation;

  const context = operation.getContext();
  const correlationId = context.correlationId;

  const isMutation =
    operation.query.definitions &&
    operation.query.definitions.some(
      (definition) =>
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'mutation'
    );

  return {
    operationName,
    isMutation,
    correlationId,
    // only include variables if query is not a mutation.
    // Mutations can contain PII/GDPR-sensitive data
    variables: isMutation ? 'mutation' : JSON.stringify(variables),
  };
};

function shouldReportError(
  error: { message: string; errorCode?: string; status?: number },
  operationContext: {
    dangerouslyDisableAllGlobalErrorReporting?: boolean;
    disableGlobalErrorReportingForMatches?: Array<{
      errorCode?: string;
      httpStatus?: number;
      errorMessage?: string;
    }>;
  }
) {
  const errorCode = error?.errorCode;
  const httpStatus = error?.status;
  const errorMessage = error?.message;

  if (operationContext?.dangerouslyDisableAllGlobalErrorReporting) {
    return false;
  } else if (
    operationContext?.disableGlobalErrorReportingForMatches &&
    Array.isArray(operationContext?.disableGlobalErrorReportingForMatches) &&
    operationContext.disableGlobalErrorReportingForMatches.length > 0
  ) {
    const errorData = {
      errorCode,
      httpStatus,
      errorMessage,
    };
    const match = operationContext.disableGlobalErrorReportingForMatches.find(
      (matcher: any) =>
        Object.keys(matcher).reduce(
          (acc, key) => {
            if (!acc) {
              return false;
            }

            const matchValue = matcher[key];
            const errorValue = errorData[key as keyof typeof errorData];

            if (matchValue === errorValue) {
              return true;
            }

            return (
              typeof errorValue === 'string' &&
              typeof matchValue === 'string' &&
              matchValue.length > 0 &&
              errorValue.includes(matchValue)
            );
          },
          Object.keys(matcher).length > 0
        )
    );
    if (match) {
      return false;
    }
  }
  return true;
}

export default new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const sub = forward(operation).subscribe({
        next(result) {
          observer.next(result);

          if (!result.errors || !result.errors.length) {
            return;
          }

          const operationContext = operation.getContext();

          for (const err of result.errors) {
            if (!shouldReportError(err, operationContext)) {
              continue;
            }
            Raven.captureException(
              new Error(`hubspot-apollo-client: ${err.message}`),
              {
                extra: {
                  ...getOperationDetails(operation),
                },
              }
            );
          }
        },
        error(err) {
          const operationDetails = getOperationDetails(operation);
          const operationContext = operation.getContext();

          if (!shouldReportError(err, operationContext)) {
            observer.error(err);
            return;
          }

          if (err.errorCode === 'TIMEOUT') {
            Raven.captureException(
              new Error('hubspot-apollo-client: graphQLTimeout'),
              {
                extra: {
                  // Hoist these 3 attributes out of the originalError so they can be filtered on in logfetch
                  errorCode: err?.errorCode,
                  httpStatus: err?.status,
                  errorMessage: err?.message,
                  ...operationDetails,
                },
              }
            );
          } else {
            Raven.captureException(
              new Error('hubspot-apollo-client: networkError'),
              {
                extra: {
                  // Hoist these 3 attributes out of the originalError so they can be filtered on in logfetch
                  errorCode: err?.errorCode,
                  httpStatus: err?.status,
                  errorMessage: err?.message,
                  ...operationDetails,
                },
              }
            );
          }

          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });

      return () => {
        if (sub) {
          sub.unsubscribe();
        }
      };
    })
);
